import classNames from 'classnames'
import React, { FC, useState } from 'react'
import { useTranslations } from '../hooks/useTranslations'
import { InternalExternalLink } from '../types/InternalExternalLink'
import { PortableTextBodyElement } from '../utilities/blockContent'
import { isNullOrWhiteSpace } from '../utilities/string'
import { AccordionButton } from './Accordion/AccordionButton'
import { Button } from './Button'
import { Collapse } from './Collapse'
import { MetricValues } from './KeyMetrics'
import { KeyMetricsList } from './KeyMetricsList'
import { LargeLink } from './LargeLink'
import { RichTextArea } from './PortableText/RichTextArea'
import { BusinessLineBlockSerializer } from './PortableText/serializers/blocks/BusinessLineBlockSerializer'
import { BusinessLineListSerializer } from './PortableText/serializers/lists/BusinessLineListSerializer'

export interface InvestmentStrategyItemProps {
  descriptionFormatted?: PortableTextBodyElement[]
  gaTrackingId?: string
  investmentRangeLabelOverride?: string
  investmentRanges?: string[]
  keyMetrics?: MetricValues[]
  link?: InternalExternalLink
  title: string
}

export const InvestmentStrategyItem: FC<InvestmentStrategyItemProps> = ({
  descriptionFormatted,
  gaTrackingId,
  investmentRangeLabelOverride,
  investmentRanges,
  keyMetrics,
  link,
  title,
}) => {
  const translations = useTranslations()
  const [expanded, setExpanded] = useState(false)

  const investmentRangeTextClass = classNames(
    'font-t-light align-middle whitespace-nowrap text-secondary-darker',
    'text-t-600',
    'sm:text-t-700',
    'md:text-t-800',
    {
      'xl:text-t-900': investmentRanges?.length === 1,
    }
  )

  return (
    <div
      className={classNames(
        'flex flex-col pt-15 pb-45 last:pb-0',
        'tb:pb-60',
        'sm:pl-30',
        'md:pl-0',
        'lg:pl-30'
      )}
    >
      <div className="">
        {!isNullOrWhiteSpace(title) && (
          <h3>
            <LargeLink
              {...link}
              additionalClassName="sm:text-t-600 xl:pb-15 xl:text-t-800"
              chevronOffset="mt-[2px] sm:mt-[4px] xl:mt-[7px]"
              url={link?.url ?? ''}
            >
              {title}
            </LargeLink>
          </h3>
        )}

        {!!investmentRanges?.length && (
          <h4 className="hidden mb-5 font-t text-t-300 text-primary tb:inline-block xl:text-t-400">
            {!isNullOrWhiteSpace(investmentRangeLabelOverride)
              ? investmentRangeLabelOverride
              : translations.investmentRange}
          </h4>
        )}
      </div>

      <button
        className="flex justify-between gap-15 group text-left outline-none focus:outline-none"
        onClick={() => setExpanded(current => !current)}
        data-ga-tracking-id={gaTrackingId}
        data-ga-tracking-name={!isNullOrWhiteSpace(gaTrackingId) ? title : undefined}
      >
        {!!investmentRanges?.length && (
          <div className="flex flex-col flex-grow w-full">
            {investmentRanges.map((range, i) => (
              <p key={i} className={investmentRangeTextClass}>
                {range}
              </p>
            ))}
          </div>
        )}

        <div className="flex flex-grow justify-end">
          <div className="flex gap-15 tb:gap-30">
            {!!keyMetrics?.length && (
              <KeyMetricsList
                additionalClassName="hidden tb:block"
                keyMetrics={keyMetrics}
              />
            )}

            <AccordionButton active={expanded} />
          </div>
        </div>
      </button>

      <Collapse
        className="transition-all duration-200"
        contentClassName="flex pt-20 tb:justify-end"
        isOpen={expanded}
      >
        <div
          className={classNames(
            'flex flex-col gap-30 w-full',
            'tb:w-[25.3125rem]',
            'md:w-[30.9375rem]',
            'xl:w-[35.9375rem]'
          )}
        >
          <div className="flex flex-col gap-20">
            {!!keyMetrics?.length && (
              <KeyMetricsList additionalClassName="tb:hidden" keyMetrics={keyMetrics} />
            )}

            {!!descriptionFormatted?.length && (
              <div className="xl:pr-30">
                <RichTextArea
                  components={{
                    block: BusinessLineBlockSerializer,
                    list: BusinessLineListSerializer,
                  }}
                  value={descriptionFormatted}
                />
              </div>
            )}
          </div>

          {!!link && !isNullOrWhiteSpace(link?.url) && (
            <div className="w-full text-center tb:text-left pb-8">
              <Button
                isExternalUrl={link.isExternal}
                title={
                  !isNullOrWhiteSpace(link.title) ? link.title : translations.more ?? ''
                }
                url={link.url}
              >
                {!isNullOrWhiteSpace(link.title) ? link.title : translations.more}
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}
